import React, { useState, useEffect, useRef } from 'react';
import {
  Plus,
  FileText,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Activity,
  User
} from 'lucide-react';
import DataTable from '../../components/DataTable/DataTable';
import { Breadcrumb } from '../../components/Common';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import commercialProposalService from '../../services/commercialProposalService';
import '../../components/ConfirmationModal/ConfirmationModal.css';
import { formatRupeeAmount, formatINR } from '../../utils/currencyFormat';

const CommercialProposal = () => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProposals: 0,
    draftProposals: 0,
    sentProposals: 0,
    underReviewProposals: 0,
    acceptedProposals: 0,
    rejectedProposals: 0,
    totalValue: 0,
    averageValue: 0,
    highValueProposals: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const hasLoadedRef = useRef(false);
  const hasShownEmptyToastRef = useRef(false);

  const columns = [
    {
      key: 'proposalCode',
      label: 'Proposal Code',
      sortable: true,
      render: (value) => (
        <div className="flex items-center space-x-2">
          <FileText size={14} className="text-blue-600" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      key: 'proposalTitle',
      label: 'Title',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.prospect?.companyName && (
            <div className="text-sm text-gray-500">{row.prospect.companyName}</div>
          )}
        </div>
      ),
    },
    {
      key: 'proposalType',
      label: 'Type',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {value}
        </span>
      ),
    },
    {
      key: 'proposalStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          Draft: 'bg-gray-100 text-gray-800',
          Sent: 'bg-blue-100 text-blue-800',
          'Under Review': 'bg-yellow-100 text-yellow-800',
          Negotiation: 'bg-orange-100 text-orange-800',
          Accepted: 'bg-green-100 text-green-800',
          Rejected: 'bg-red-100 text-red-800',
          Expired: 'bg-gray-100 text-gray-800',
          Withdrawn: 'bg-gray-100 text-gray-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        );
      },
    },
    {
      key: 'totalValue',
      label: 'Value',
      sortable: true,
      render: (value, row) => (
        <div className="text-right">
          <div className="font-medium text-gray-900">
            {formatRupeeAmount(value || 0)}
          </div>
          <div className="text-sm text-gray-500">{row.currency || 'INR'}</div>
        </div>
      ),
    },
    {
      key: 'proposalOwner',
      label: 'Owner',
      sortable: true,
      render: (value) => (
        <div className="text-gray-900">{value?.firstName} {value?.lastName || 'N/A'}</div>
      ),
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      sortable: true,
      render: (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        const today = new Date();
        const isExpiring = date <= new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
        const isExpired = date < today;
        
        return (
          <div className={`text-sm ${isExpired ? 'text-red-600' : isExpiring ? 'text-orange-600' : 'text-gray-600'}`}>
            {date.toLocaleDateString()}
            {isExpired && <div className="text-xs text-red-500">Expired</div>}
            {isExpiring && !isExpired && <div className="text-xs text-orange-500">Expiring Soon</div>}
          </div>
        );
      },
    },
    {
      key: 'probability',
      label: 'Probability',
      sortable: true,
      render: (value) => (
        <div className="text-right">
          <div className="font-medium text-gray-900">{value || 0}%</div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${value || 0}%` }}
            ></div>
          </div>
        </div>
      ),
    }
  ];

  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData();
      hasLoadedRef.current = true;
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [proposalsResponse, statsResponse] = await Promise.all([
        commercialProposalService.getAllProposals(),
        commercialProposalService.getProposalStats(),
      ]);

      if (proposalsResponse.success) {
        setData(proposalsResponse.data.proposals || []);
        if (proposalsResponse.data.proposals.length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No commercial proposals found. Create your first proposal to get started.');
          hasShownEmptyToastRef.current = true;
        }
      } else {
        toast.error(proposalsResponse.message || 'Failed to load commercial proposals');
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load commercial proposal data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate('/crm/commercial-proposal/new');
  };

  const handleEdit = (row) => {
    navigate('/crm/commercial-proposal/edit', { state: { proposalData: row } });
  };

  const handleView = (row) => {
    navigate('/crm/commercial-proposal/view', { state: { proposalData: row } });
  };

  const handleDelete = (row) => {
    setDeleteItem(row);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      const response = await commercialProposalService.deleteProposal(deleteItem._id);
      if (response.success) {
        toast.success('Commercial proposal deleted successfully');
        loadData();
        setShowDeleteModal(false);
        setDeleteItem(null);
      } else {
        toast.error(response.message || 'Failed to delete commercial proposal');
      }
    } catch (error) {
      console.error('Error deleting commercial proposal:', error);
      toast.error('Failed to delete commercial proposal');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select commercial proposals to delete');
      return;
    }

    try {
      const response = await commercialProposalService.bulkDeleteProposals(selectedRows);
      if (response.success) {
        toast.success(response.message);
        setSelectedRows([]);
        loadData();
      } else {
        toast.error(response.message || 'Failed to delete commercial proposals');
      }
    } catch (error) {
      console.error('Error bulk deleting commercial proposals:', error);
      toast.error('Failed to delete commercial proposals');
    }
  };

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/dashboard' },
          { label: 'CRM', path: '/crm' },
          { label: 'Commercial Proposal' },
        ]}
      />

      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title">
            <h1>Commercial Proposal</h1>
            <p>Manage and track all commercial proposals and quotes</p>
          </div>
          <div className="page-actions">
            <button className="btn-primary" onClick={handleAdd}>
              <Plus size={16} />
              Add Proposal
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalProposals || 0}</div>
          <div className="metric-title">Total Proposals</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{formatRupeeAmount((stats.totalValue || 0))}</div>
          <div className="metric-title">Total Value</div>
          <div className="metric-subtitle">All proposals</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.acceptedProposals || 0}</div>
          <div className="metric-title">Accepted</div>
          <div className="metric-subtitle">Successful proposals</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Activity size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.highValueProposals || 0}</div>
          <div className="metric-title">High Value</div>
          <div className="metric-subtitle">₹1L+ proposals</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search proposals..."
          emptyMessage="No commercial proposals found. Create your first proposal to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Commercial Proposal"
        message={`Are you sure you want to delete proposal "${deleteItem?.proposalCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default CommercialProposal;
